import React from "react";
import { useNavigate } from "react-router-dom";

const LenderNavigationGuardContext = React.createContext(null);

export function LenderNavigationGuardProvider({ children }) {
  const navigate = useNavigate();

  const activeGuardRef = React.useRef(null);

  const registerNavigationGuard = React.useCallback((guardFunction) => {
    activeGuardRef.current = guardFunction;

    return () => {
      if (activeGuardRef.current === guardFunction) {
        activeGuardRef.current = null;
      }
    };
  }, []);

  const requestNavigation = React.useCallback(
    (targetPath, navigateOptions = {}) => {
      const activeGuard = activeGuardRef.current;

      if (activeGuard) {
        const navigationWasBlocked = activeGuard({
          targetPath,
          navigateOptions,
        });

        if (navigationWasBlocked) {
          return false;
        }
      }

      navigate(targetPath, navigateOptions);

      return true;
    },
    [navigate],
  );

  const value = React.useMemo(
    () => ({
      requestNavigation,
      registerNavigationGuard,
    }),
    [registerNavigationGuard, requestNavigation],
  );

  return (
    <LenderNavigationGuardContext.Provider value={value}>
      {children}
    </LenderNavigationGuardContext.Provider>
  );
}

export function useLenderNavigationGuard() {
  const context = React.useContext(LenderNavigationGuardContext);

  if (!context) {
    throw new Error(
      "useLenderNavigationGuard must be used inside LenderNavigationGuardProvider.",
    );
  }

  return context;
}
